import classNames from "classnames";
import * as React from "react";
import { Link, LinkProps } from "react-router-dom";
import styled, { css } from "styled-components";

import {
  BaseFavouriteBtn,
  BaseFavouriteBtnProps,
  EventBadge,
  ResultCardSubHeading,
  SvgSprite,
  VolunteerSearchLink,
  VolunteerSearchLinkProps,
} from "@skv/core";
import { ParseRemark } from "@skv/core/src/ui";

import { OpportunitySearchQueryState } from "@skv/state";
import { FAVOURITES_ENUM } from "@skv/state/src/state/favourites";
import { getDateRange, isInThePast, resizeImg } from "@skv/util";
import { formatDistanceToNow } from "date-fns";

const ResultListSvgSprite = styled(SvgSprite)`
  vertical-align: middle;
`;

export const ResultCardHeader = styled.header.attrs((props) => ({
  ...props,
  className: props.className ?? "mb-2",
}))``;

export const ResultCard = styled.article<{
  $isFeatured?: boolean;
  $isNew?: boolean;
  $hover?: boolean;
}>`
  position: relative;
  overflow-wrap: break-word;
  padding-left: 0.5rem;
  padding-right: 0.5rem;
  margin-left: -0.5rem;

  li > & {
    border-top: none;
    border-bottom: 1px solid ${(p) => p.theme.gray300};
  }

  @media (min-width: ${(p) => p.theme.lgBreakpoint}) {
    padding-left: 1rem;
    padding-right: 1rem;
    margin-left: -1rem;
  }

  ${(p) =>
    p.$hover !== false &&
    css`
      &:hover {
        background-color: ${(p) => p.theme.gray100};
      }
    `}

  ${(p) => {
    if (p.$isFeatured) {
      return css`
        box-shadow: -2px 0 0 var(--danger);

        ${ResultCardHeader}:before {
          content: "In Demand";
          display: inline-flex;
          float: right;
          padding: 0 0 0.5rem 0.5rem;
          font-size: 0.875rem;
          font-weight: bold;
          color: var(--danger);
        }
      `;
    }
  }}
`;

const SearchResultStretchedLink = styled(Link)<{
  to: LinkProps["to"];
}>`
  &:after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
`;

export const ResultCardHeading = styled.h3.attrs((p) => p)``;

type HeadingProps = React.HTMLAttributes<HTMLHeadingElement>;

export interface ResultCardLinkHeadingProps
  extends Omit<HeadingProps, "title"> {
  title: React.ReactNode;
  to: LinkProps["to"];
  stretchedLink?: boolean;
}

export const ResultCardLinkHeading: React.FC<ResultCardLinkHeadingProps> = ({
  title,
  to,
  stretchedLink,
  ...rest
}) => {
  return (
    <ResultCardHeading {...rest}>
      {stretchedLink ? (
        <SearchResultStretchedLink to={to}>{title}</SearchResultStretchedLink>
      ) : (
        <Link to={to}>{title}</Link>
      )}
    </ResultCardHeading>
  );
};

export interface ResultCardVolunteerSearchLinkHeadingProps
  extends Omit<HeadingProps, "title"> {
  title: React.ReactNode;
  to: Exclude<VolunteerSearchLinkProps["to"], undefined>;
  searchQuery: OpportunitySearchQueryState;
  stretchedLink?: boolean;
  shouldUpdateSearchForm?: VolunteerSearchLinkProps["shouldUpdateSearchForm"];
}

export const ResultCardVolunteerSearchLinkHeading: React.FC<
  ResultCardVolunteerSearchLinkHeadingProps
> = ({
  title,
  to,
  searchQuery,
  stretchedLink = true,
  shouldUpdateSearchForm,
  ...rest
}) => {
  return (
    <ResultCardHeading {...rest}>
      {stretchedLink ? (
        <SearchResultStretchedLink
          as={VolunteerSearchLink}
          to={to}
          searchQuery={searchQuery}
          shouldUpdateSearchForm={shouldUpdateSearchForm}
        >
          {title}
        </SearchResultStretchedLink>
      ) : (
        <VolunteerSearchLink
          to={to}
          searchQuery={searchQuery}
          shouldUpdateSearchForm={shouldUpdateSearchForm}
        >
          {title}
        </VolunteerSearchLink>
      )}
    </ResultCardHeading>
  );
};

interface ResultCardLinkSubHeadingProps extends Omit<HeadingProps, "title"> {
  title: React.ReactNode;
  to?: LinkProps["to"];
  organisationMemberName?: string;
}

export const ResultCardLinkSubHeading: React.FC<
  ResultCardLinkSubHeadingProps
> = ({ title, to, organisationMemberName, ...rest }) => {
  return (
    <ResultCardSubHeading {...rest}>
      {organisationMemberName && (
        <>
          {organisationMemberName}
          <i className="font-weight-normal h6"> supported by </i>
        </>
      )}
      {to ? (
        <Link className="h3 text-dark text-decoration-none" to={to}>
          {title}
        </Link>
      ) : (
        <span className="text-dark">{title}</span>
      )}
    </ResultCardSubHeading>
  );
};

const BaseResultMetaListItem = styled.li.attrs<{ small?: boolean }>(
  ({ small = true, className, ...props }) => ({
    className: classNames(
      "list-inline-item",
      {
        small: small,
      },
      className
    ),
    ...props,
  })
)`
  margin-bottom: 0.5rem;
`;

type LiElementProps = React.DetailedHTMLProps<
  React.LiHTMLAttributes<HTMLLIElement>,
  HTMLLIElement
>;

interface ResultMetaListItemProps extends LiElementProps {
  icon?: "marker" | "ribbon" | "briefcase" | "calendar";
  small?: boolean;
}

export const ResultMetaListItem: React.FC<ResultMetaListItemProps> = ({
  children,
  icon,
  small,
  className,
}) => {
  const altText =
    icon === "marker"
      ? "Location: "
      : icon === "ribbon"
      ? "Category: "
      : icon === "briefcase"
      ? "Types of work: "
      : null;

  return (
    <BaseResultMetaListItem
      small={small}
      className={className}
      css={`
        a {
          text-decoration: none;
        }
      `}
    >
      {icon && (
        <ResultListSvgSprite
          id={`icons:${icon}`}
          width={16}
          height={16}
          aria-label={altText || undefined}
          className="mr-1"
        />
      )}
      {children}
    </BaseResultMetaListItem>
  );
};

interface ResultMetaListProps {
  $listItemSpacing?: string;
}

export const ResultMetaList = styled.ul.attrs<ResultMetaListProps>((props) => ({
  ...props,
  className: classNames("list-inline", props.className),
}))<ResultMetaListProps>`
  ${BaseResultMetaListItem}:not(:last-child) {
    margin-right: ${(p) => p.$listItemSpacing || "1rem"};
  }

  margin-bottom: 0;
`;

/* TODO : Revisit this isolated ml-n3 class */
export const ResultCardFooter = styled.footer.attrs((props) => ({
  ...props,
  className: props.className || "ml-n3 d-flex align-items-center",
}))``;

/*
  Full components that are built from ResultCard
*/

interface OpportunityResultCardProps {
  name: React.ReactNode;
  to: ResultCardLinkHeadingProps["to"];

  organisationName: React.ReactNode;
  organisationTo: ResultCardLinkSubHeadingProps["to"];

  organisationMemberName?: string;

  locations?: React.ReactNode;
  category?: React.ReactNode;
  typesOfWork?: React.ReactNode;

  summary: string;

  logoUrl?: string;
  isFeatured?: boolean;
  isNew?: boolean;
  eventStartDate?: string;
  eventEndDate?: string;

  isFavourited: boolean;
  onFavourite: BaseFavouriteBtnProps["onFavourite"];
  onUnfavourite: BaseFavouriteBtnProps["onUnfavourite"];

  headingProps?: Partial<Parameters<typeof ResultCardLinkHeading>[0]>;
  subHeadingProps?: Partial<Parameters<typeof ResultCardLinkSubHeading>[0]>;
}

export const OpportunityResultCard: React.FC<OpportunityResultCardProps> = ({
  name,
  to,
  organisationName,
  organisationTo,
  organisationMemberName,
  locations,
  category,
  typesOfWork,
  summary,
  logoUrl,
  isFeatured = false,
  isNew = false,
  isFavourited,
  eventStartDate,
  eventEndDate,
  headingProps,
  subHeadingProps,
  onFavourite,
  onUnfavourite,
}) => {
  return (
    <ResultCard $isFeatured={isFeatured} $isNew={isNew} className="pt-4 pb-2">
      <ResultCardHeader>
        <ResultCardLinkHeading
          title={name}
          to={to}
          {...headingProps}
          stretchedLink={true}
        />
        <ResultCardLinkSubHeading
          title={organisationName}
          to={organisationTo}
          organisationMemberName={organisationMemberName}
          {...subHeadingProps}
        />
      </ResultCardHeader>

      {eventStartDate && (
        <ResultMetaList $listItemSpacing="3rem">
          {isInThePast(eventStartDate) === false && (
            <ResultMetaListItem>
              <EventBadge date={eventStartDate} />
            </ResultMetaListItem>
          )}
          <ResultMetaListItem icon="calendar">
            {getDateRange(eventStartDate, eventEndDate)}
          </ResultMetaListItem>
        </ResultMetaList>
      )}

      <ResultMetaList $listItemSpacing="3rem">
        {locations && (
          <ResultMetaListItem icon="marker">{locations}</ResultMetaListItem>
        )}
        {category && (
          <ResultMetaListItem icon="ribbon">{category}</ResultMetaListItem>
        )}
        {typesOfWork && (
          <ResultMetaListItem icon="briefcase">
            {typesOfWork}
          </ResultMetaListItem>
        )}
      </ResultMetaList>

      <ParseRemark>{summary}</ParseRemark>

      <ResultCardFooter>
        <BaseFavouriteBtn
          isFavourited={isFavourited}
          onFavourite={onFavourite}
          onUnfavourite={onUnfavourite}
          type={FAVOURITES_ENUM.OPPORTUNITY}
        />
        {logoUrl && (
          <img
            className="img-fluid my-2 ml-auto"
            srcSet={`${resizeImg(logoUrl, "180x120")} 2x`}
            src={resizeImg(logoUrl, "90x60")}
            alt={`Logo for ${organisationName}`}
          />
        )}
      </ResultCardFooter>
    </ResultCard>
  );
};

interface OrganisationResultCardProps {
  organisationName: string;
  to: ResultCardLinkHeadingProps["to"];
  category: React.ReactNode;
  headingProps?: Partial<Parameters<typeof ResultCardLinkHeading>[0]>;
}

export const OrganisationResultCard: React.FC<OrganisationResultCardProps> = ({
  organisationName,
  to,
  category,
  headingProps,
}) => {
  return (
    <ResultCard className="py-3">
      <ResultCardHeader>
        <ResultCardLinkHeading
          to={to}
          title={organisationName}
          {...headingProps}
        />
      </ResultCardHeader>
      <ResultMetaList>
        {category && (
          <ResultMetaListItem icon="ribbon">{category}</ResultMetaListItem>
        )}
      </ResultMetaList>
    </ResultCard>
  );
};

interface OrganisationFavouriteResultCardProps {
  organisationName: React.ReactNode;
  to: ResultCardLinkHeadingProps["to"];
  locations?: React.ReactNode;
  category?: React.ReactNode;
  isFavourited: boolean;
  onFavourite: BaseFavouriteBtnProps["onFavourite"];
  onUnfavourite: BaseFavouriteBtnProps["onUnfavourite"];
  logoUrl?: string;
  summary: string;
  headingProps?: Partial<Parameters<typeof ResultCardLinkHeading>[0]>;
}

export const OrganisationFavouriteResultCard: React.FC<
  OrganisationFavouriteResultCardProps
> = ({
  to,
  isFavourited,
  onFavourite,
  onUnfavourite,
  organisationName,
  locations,
  category,
  logoUrl,
  summary,
  headingProps,
}) => {
  return (
    <ResultCard className="py-3">
      <ResultCardHeader>
        <ResultCardLinkHeading
          to={to}
          title={organisationName}
          {...headingProps}
          stretchedLink={true}
        />
      </ResultCardHeader>
      <ResultMetaList>
        {locations && (
          <ResultMetaListItem icon="marker">{locations}</ResultMetaListItem>
        )}
        {category && (
          <ResultMetaListItem icon="ribbon">{category}</ResultMetaListItem>
        )}
      </ResultMetaList>

      <ParseRemark>{summary}</ParseRemark>

      <ResultCardFooter>
        <BaseFavouriteBtn
          isFavourited={isFavourited}
          onFavourite={onFavourite}
          onUnfavourite={onUnfavourite}
          type={FAVOURITES_ENUM.ORGANISATION}
        />
        {logoUrl && (
          <img
            className="img-fluid my-2 ml-auto"
            srcSet={`${resizeImg(logoUrl, "180x120")} 2x`}
            src={resizeImg(logoUrl, "90x60")}
            alt={`Logo for ${organisationName}`}
          />
        )}
      </ResultCardFooter>
    </ResultCard>
  );
};

interface OpportunityFavouriteResultCardProps {
  name: React.ReactNode;
  to: ResultCardLinkHeadingProps["to"];
  organisationName: React.ReactNode;
  locations?: React.ReactNode;
  category?: React.ReactNode;
  typesOfWork?: React.ReactNode;
  summary: string;
  logoUrl?: string;
  isFavourited: boolean;
  onFavourite: BaseFavouriteBtnProps["onFavourite"];
  onUnfavourite: BaseFavouriteBtnProps["onUnfavourite"];
  headingProps?: Partial<Parameters<typeof ResultCardLinkHeading>[0]>;
  subHeadingProps?: Partial<Parameters<typeof ResultCardLinkSubHeading>[0]>;
  date?: string;
}

export const OpportunityFavouriteResultCard: React.FC<
  OpportunityFavouriteResultCardProps
> = ({
  name,
  to,
  organisationName,
  locations,
  category,
  typesOfWork,
  summary,
  logoUrl,
  isFavourited,
  onFavourite,
  onUnfavourite,
  headingProps,
  subHeadingProps,
  date,
}) => {
  const formatDateDistance = (dateString) => {
    const formattedDate = formatDistanceToNow(new Date(dateString), {
      addSuffix: true,
    });
    return formattedDate;
  };

  return (
    <ResultCard className="py-3">
      <ResultCardHeader>
        {date ? (
          <div className="d-flex align-items-center justify-content-between">
            <ResultCardLinkHeading
              to={to}
              title={name}
              stretchedLink={true}
              {...headingProps}
            />
            <span className="text-dark">{formatDateDistance(date)}</span>
          </div>
        ) : (
          <ResultCardLinkHeading
            to={to}
            title={name}
            stretchedLink={true}
            {...headingProps}
          />
        )}
        <ResultCardLinkSubHeading
          title={organisationName}
          {...subHeadingProps}
        />
      </ResultCardHeader>
      <ResultMetaList $listItemSpacing="3rem">
        {locations && (
          <ResultMetaListItem icon="marker">{locations}</ResultMetaListItem>
        )}
        {category && (
          <ResultMetaListItem icon="ribbon">{category}</ResultMetaListItem>
        )}
        {typesOfWork && (
          <ResultMetaListItem icon="briefcase">
            {typesOfWork}
          </ResultMetaListItem>
        )}
      </ResultMetaList>

      <ParseRemark>{summary}</ParseRemark>

      <ResultCardFooter>
        <BaseFavouriteBtn
          isFavourited={isFavourited}
          onFavourite={onFavourite}
          onUnfavourite={onUnfavourite}
          type={FAVOURITES_ENUM.OPPORTUNITY}
        />
        {logoUrl && (
          <img
            className="img-fluid my-2 ml-auto"
            srcSet={`${resizeImg(logoUrl, "180x120")} 2x`}
            src={resizeImg(logoUrl, "90x60")}
            alt={`Logo for ${organisationName}`}
          />
        )}
      </ResultCardFooter>
    </ResultCard>
  );
};

interface OrganisationDetailsOpportunityResultCardProps {
  name: React.ReactNode;
  to: ResultCardLinkHeadingProps["to"];
  locations?: React.ReactNode;
  category?: React.ReactNode;
  typesOfWork?: React.ReactNode;
  headingProps?: Partial<Parameters<typeof ResultCardLinkHeading>[0]>;
}

export const OrganisationDetailsOpportunityResultCard: React.FC<
  OrganisationDetailsOpportunityResultCardProps
> = ({ name, to, locations, category, typesOfWork, headingProps }) => {
  return (
    <ResultCard className="py-3">
      <ResultCardHeader>
        <ResultCardLinkHeading
          to={to}
          title={name}
          className="h5 mb-0"
          {...headingProps}
        />
      </ResultCardHeader>
      <ResultMetaList>
        {locations && (
          <ResultMetaListItem icon="marker">{locations}</ResultMetaListItem>
        )}
        {category && (
          <ResultMetaListItem icon="ribbon">{category}</ResultMetaListItem>
        )}
        {typesOfWork && (
          <ResultMetaListItem icon="briefcase">
            {typesOfWork}
          </ResultMetaListItem>
        )}
      </ResultMetaList>
    </ResultCard>
  );
};
